import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Search,
  Visibility,
  Cancel,
  Download,
  FilterList,
  PictureAsPdf,
  Code,
} from '@mui/icons-material';
import { invoiceService, resolvePdfUrl } from '../../../services/api';

// Estados de DTE para mostrar como chips coloreados
const STATUS_COLORS = {
  draft: 'default',
  signed: 'info',
  sent: 'primary',
  accepted: 'success',
  rejected: 'error',
  contingency: 'warning',
  cancelled: 'error',
  processing: 'warning',
};

const STATUS_LABELS = {
  draft: 'Borrador',
  signed: 'Firmado',
  sent: 'Enviado',
  accepted: 'Aceptado',
  rejected: 'Rechazado',
  contingency: 'Contingencia',
  cancelled: 'Anulado',
  processing: 'Procesando',
};

const DTEByDate = () => {
  // Obtener fecha de hoy en formato YYYY-MM-DD
  const today = new Date().toISOString().split('T')[0];
  const [fechaInicio, setFechaInicio] = useState(today);
  const [fechaFin, setFechaFin] = useState(today);
  const [tipoDte, setTipoDte] = useState('');
  const [codigoGeneracion, setCodigoGeneracion] = useState('');
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Estado para el modal de detalle
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentDocId, setCurrentDocId] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  // Buscar documentos por fecha
  const handleSearch = async () => {
    if (!fechaInicio || !fechaFin) {
      alert('Por favor seleccione ambas fechas');
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      // Llamar al backend
      const filtros = {
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
      };
      
      if (tipoDte) {
        filtros.tipo = tipoDte;
      }
      
      if (codigoGeneracion) {
        filtros.codigo_generacion = codigoGeneracion;
      }
      
      const data = await invoiceService.getAll(filtros);
      
      // Transformar datos del backend al formato del componente
      setDocuments(
        Array.isArray(data) 
          ? data.map((doc) => {
              // Extraer fecEmi y horEmi del JSON del DTE
              const fecEmi = doc.json_data?.identificacion?.fecEmi;
              const horEmi = doc.json_data?.identificacion?.horEmi;
              
              // Formatear fecha: DD/MM/YYYY HH:MM:SS
              let fechaFormateada = '';
              if (fecEmi) {
                // fecEmi viene en formato YYYY-MM-DD
                const fechaParts = fecEmi.split('-');
                if (fechaParts.length === 3) {
                  const fechaDMY = `${fechaParts[2]}/${fechaParts[1]}/${fechaParts[0]}`;
                  fechaFormateada = horEmi ? `${fechaDMY} ${horEmi}` : fechaDMY;
                }
              } else {
                // Fallback al formato original de la base de datos
                fechaFormateada = doc.fecha ? doc.fecha.split('T')[0] : '';
              }
              
              return {
                id: doc.id,
                tipoDte: doc.tipo_dte || doc.tipo,
                numeroControl: doc.numero_control || `${doc.serie}-${String(doc.correlativo).padStart(8, '0')}`,
                codigoGeneracion: doc.json_data?.identificacion?.codigoGeneracion || doc.codigo_generacion || '',
                fechaEmision: fechaFormateada,
                receptor: doc.cliente_nombre || 'Consumidor Final',
                montoTotal: parseFloat(doc.monto) + parseFloat(doc.iva || 0),
                estado: doc.estado === 'activa' ? 'accepted' : (doc.estado === 'anulada' ? 'cancelled' : 'draft'),
              };
            })
          : []
      );
    } catch (error) {
      console.error('Error buscando documentos:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  // Obtener nombre del tipo de DTE
  const getTipoDteName = (code) => {
    const types = {
      '01': 'Factura',
      '03': 'CCF',
      '04': 'Nota de Remisión',
      '05': 'Nota de Crédito',
      '06': 'Nota de Débito',
      '07': 'Retención',
      '14': 'Sujeto Excluido',
    };
    return types[code] || code;
  };
  
  // Ver detalle del documento
  const handleViewDetail = async (docId) => {
    setCurrentDocId(docId);
    setDetailOpen(true);
    setDetailLoading(true);
    setDetailData(null);
    
    try {
      const result = await invoiceService.getById(docId);
      setDetailData(result);
    } catch (error) {
      console.error('Error obteniendo detalle:', error);
      setDetailData({ error: error.message });
    } finally {
      setDetailLoading(false);
    }
  };
  
  // Generar PDF para la factura actual
  const handleGeneratePdf = async () => {
    if (!currentDocId) return;
    
    setGeneratingPdf(true);
    try {
      const result = await invoiceService.generatePdf(currentDocId);
      if (result.success && result.pdfUrl) {
        // Actualizar los datos del detalle con la nueva URL del PDF
        setDetailData(prev => ({
          ...prev,
          tipo: 'pdf',
          pdfUrl: result.pdfUrl
        }));
      } else {
        alert(result.error || 'Error al generar PDF');
      }
    } catch (error) {
      console.error('Error generando PDF:', error);
      alert('Error al generar PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Consultar Documentos por Fecha
      </Typography>

      {/* Filtros de búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Fecha Inicio"
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                label="Fecha Fin"
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={2.5}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo</InputLabel>
                <Select
                  value={tipoDte}
                  label="Tipo"
                  onChange={(e) => setTipoDte(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="01">Factura</MenuItem>
                  <MenuItem value="03">CCF</MenuItem>
                  <MenuItem value="07">Retención</MenuItem>
                  <MenuItem value="14">Excluido</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Código Generación"
                value={codigoGeneracion}
                onChange={(e) => setCodigoGeneracion(e.target.value)}
                placeholder="Código"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={1.5}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                disabled={loading}
                size="small"
              >
                {loading ? '...' : 'Buscar'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Resultados */}
      {hasSearched && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Resultados de la Búsqueda
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {documents.length} documento(s) encontrado(s)
            </Typography>

            {documents.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Número de Control</TableCell>
                      <TableCell>Código Generación</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Receptor</TableCell>
                      <TableCell align="right">Monto</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id} hover>
                        <TableCell>{getTipoDteName(doc.tipoDte)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {doc.numeroControl}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {doc.codigoGeneracion || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{doc.fechaEmision}</TableCell>
                        <TableCell>{doc.receptor}</TableCell>
                        <TableCell align="right">${doc.montoTotal.toFixed(2)}</TableCell>
                        <TableCell>
                          <Chip
                            label={STATUS_LABELS[doc.estado]}
                            color={STATUS_COLORS[doc.estado]}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            color="primary" 
                            title="Ver detalle"
                            onClick={() => handleViewDetail(doc.id)}
                          >
                            <Visibility />
                          </IconButton>
                          {doc.estado === 'accepted' && (
                            <IconButton size="small" color="error" title="Anular">
                              <Cancel />
                            </IconButton>
                          )}
                          <IconButton size="small" title="Descargar">
                            <Download />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <FilterList sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No se encontraron documentos en el rango de fechas seleccionado
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Modal de detalle del documento */}
      <Dialog 
        open={detailOpen} 
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalle del Documento
          {detailData?.tipo === 'pdf' && (
            <Chip 
              icon={<PictureAsPdf />} 
              label="PDF" 
              color="primary" 
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
          {detailData?.tipo === 'json' && (
            <Chip 
              icon={<Code />} 
              label="JSON" 
              color="secondary" 
              size="small" 
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Typography>Cargando...</Typography>
          ) : detailData?.error ? (
            <Typography color="error">Error: {detailData.error}</Typography>
          ) : detailData ? (
            <Box>
              {/* Si hay PDF, mostrar botón para abrir */}
              {detailData.pdfUrl ? (
                <Box sx={{ mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                  <Button 
                    variant="contained" 
                    startIcon={<PictureAsPdf />}
                    href={resolvePdfUrl(detailData.pdfUrl)}
                    target="_blank"
                    fullWidth
                  >
                    Abrir PDF
                  </Button>
                </Box>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Button 
                    variant="outlined" 
                    startIcon={<PictureAsPdf />}
                    onClick={handleGeneratePdf}
                    disabled={generatingPdf}
                    fullWidth
                  >
                    {generatingPdf ? 'Generando PDF...' : 'GENERAR PDF'}
                  </Button>
                </Box>
              )}
              
              {/* Datos de la factura */}
              <Typography variant="h6" gutterBottom>
                Datos del Documento
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary="ID" secondary={detailData.factura?.id} />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="Tipo" secondary={detailData.factura?.tipo} />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="Serie" secondary={detailData.factura?.serie} />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="Correlativo" secondary={detailData.factura?.correlativo} />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="Fecha" secondary={detailData.factura?.fecha} />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText 
                    primary="Monto" 
                    secondary={`${(parseFloat(detailData.factura?.monto || 0) + parseFloat(detailData.factura?.iva || 0)).toFixed(2)}`} 
                  />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="IVA" secondary={`${parseFloat(detailData.factura?.iva || 0).toFixed(2)}`} />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="Estado" secondary={detailData.factura?.estado} />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="Cliente" secondary={detailData.factura?.cliente_nombre || 'Consumidor Final'} />
                </ListItem>
                <Divider component="li" />
                <ListItem>
                  <ListItemText primary="NIT" secondary={detailData.factura?.cliente_nit || 'N/A'} />
                </ListItem>
              </List>
              
              {/* Si hay JSON data, mostrarlo */}
              {detailData.factura?.json_data && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Datos JSON
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      bgcolor: '#f5f5f5',
                      maxHeight: 300,
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem'
                    }}
                  >
                    <pre>{JSON.stringify(detailData.factura.json_data, null, 2)}</pre>
                  </Paper>
                </Box>
              )}
            </Box>
          ) : (
            <Typography>No hay datos disponibles</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DTEByDate;
